import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import * as checkoutStore from '@cartwhl/common/stores/checkoutStore';
import * as productStore from '@cartwhl/common/stores/productStore';
import { isPro, isUnlimited } from '@cartwhl/common/stores/userStore';
import ProBadge from '@cartwhl/ui/atoms/ProBadge';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

export default function SpinsExplainedModal({ close }: { close: () => void }) {
  const { products } = useSnapshot(productStore.state);

  const quantity = 1;

  const proPriceObject = productStore.getPriceByID(
    products?.pro?.default_price ?? '',
  );
  const unlimitedPriceObject = productStore.getPriceByID(
    products?.unlimited?.default_price ?? '',
  );
  const proPrice = proPriceObject?.unit_amount ?? 3900;
  const unlimitedPrice = unlimitedPriceObject?.unit_amount ?? 500;
  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  const handleProCheckout = async () => {
    const hostedCheckoutURL = await checkoutStore.completeHostedCheckout(
      proPriceObject,
      quantity,
    );
    if (hostedCheckoutURL) {
      window.location.href = hostedCheckoutURL;
    }
  };

  const handleUnlimitedCheckout = async () => {
    const hostedCheckoutURL = await checkoutStore.completeHostedCheckout(
      unlimitedPriceObject,
      quantity,
    );
    if (hostedCheckoutURL) {
      window.location.href = hostedCheckoutURL;
    }
  };

  function handleOpenChange(open: boolean) {
    if (!open) {
      close();
    }
  }

  return (
    <Modal
      title="What are Spins?"
      maxWidth={684}
      open={true}
      onOpenChange={handleOpenChange}
    >
      <Text as="p" variant="studio/body/md">
        Spins are credits used in Cartwheel to generate motion and download
        assets. Spins regenerate monthly in accordance to your plan. Regardless
        of your plan, you're always able to purchase more. Unused Spins don't
        rollover.
      </Text>
      <div className="mb-2 mt-6">
        <Text as="span" variant="studio/title/md">
          Spin costs
        </Text>
      </div>
      <div className="relative rounded-lg border p-6">
        <div
          className={`mb-8 flex w-full flex-row flex-nowrap items-start gap-4 border-b pb-4`}
        >
          <div>
            <div>
              <Text
                as="span"
                variant="studio/body/medium/md"
                className="text-gray-90 dark:text-white"
              >
                Pose rendering
              </Text>
            </div>
            <Text
              as="p"
              variant="studio/body/md"
              className="max-w-[374px] py-1 opacity-60"
            >
              Rendering a pose will cost 10 Spins.
            </Text>
          </div>
          <div className="ml-auto">
            <Text as="div" variant="studio/title/md">
              10 Spins
            </Text>
          </div>
        </div>
        <div
          className={`mb-8 flex w-full flex-row flex-nowrap items-start gap-4 border-b pb-4`}
        >
          <div>
            <div>
              <Text
                as="span"
                variant="studio/body/medium/md"
                className="text-gray-90 dark:text-white"
              >
                Text to motion
              </Text>
            </div>
            <Text
              as="p"
              variant="studio/body/md"
              className="max-w-[374px] py-1 opacity-60"
            >
              Generating motion from a text prompt will cost 25 Spins.
            </Text>
          </div>
          <div className="ml-auto">
            <Text as="div" variant="studio/title/md">
              25 Spins
            </Text>
          </div>
        </div>
        <div
          className={`mb-8 flex w-full flex-row flex-nowrap items-start gap-4 border-b pb-4`}
        >
          <div>
            <div>
              <Text
                as="span"
                variant="studio/body/medium/md"
                className="text-gray-90 dark:text-white"
              >
                Generate character
              </Text>
            </div>
            <Text
              as="p"
              variant="studio/body/md"
              className="max-w-[374px] py-1 opacity-60"
            >
              Generating a new character will cost 130 Spins.
            </Text>
          </div>
          <div className="ml-auto">
            <Text as="div" variant="studio/title/md">
              130 Spins
            </Text>
          </div>
        </div>
        <div
          className={`mb-8 flex w-full flex-row flex-nowrap items-start gap-4 border-b pb-4`}
        >
          <div>
            <div>
              <Text
                as="span"
                variant="studio/body/medium/md"
                className="text-gray-90 dark:text-white"
              >
                CopyCat
              </Text>
            </div>
            <Text
              as="p"
              variant="studio/body/md"
              className="max-w-[374px] py-1 opacity-60"
            >
              Generating motion from a video stream or upload will cost 50
              Spins.{' '}
            </Text>
          </div>
          <div className="ml-auto">
            <Text as="div" variant="studio/title/md">
              50 Spins
            </Text>
          </div>
        </div>
        <div className={`flex w-full flex-row flex-nowrap items-start gap-4`}>
          <div>
            <div>
              <Text
                as="span"
                variant="studio/body/medium/md"
                className="text-gray-90 dark:text-white"
              >
                Export
              </Text>
            </div>
            <Text
              as="p"
              variant="studio/body/md"
              className="max-w-[374px] py-1 opacity-60"
            >
              Exporting motion will vary depending on the desired filetype. See
              the list below for the filetype and spin cost breakdown.
            </Text>

            <div className="mt-4">
              <ul className="marker:text-gray-60 list-disc pl-4">
                <li>
                  <Text
                    as="span"
                    variant="studio/body/md"
                    className="max-w-[374px] opacity-60"
                  >
                    Image or Video →{' '}
                  </Text>
                  <Text
                    as="span"
                    variant="studio/body/bold/md"
                    className="max-w-[374px]"
                  >
                    0 Spins
                  </Text>
                </li>
                <li>
                  <Text
                    as="span"
                    variant="studio/body/md"
                    className="max-w-[374px] opacity-60"
                  >
                    BVH, FBX, glTF →{' '}
                  </Text>
                  <Text
                    as="span"
                    variant="studio/body/bold/md"
                    className="max-w-[374px]"
                  >
                    50 Spins
                  </Text>
                </li>
                <li>
                  <Text
                    as="span"
                    variant="studio/body/md"
                    className="max-w-[374px] opacity-60"
                  >
                    Maya →{' '}
                  </Text>
                  <Text
                    as="span"
                    variant="studio/body/bold/md"
                    className="max-w-[374px]"
                  >
                    100 Spins
                  </Text>
                  <ProBadge className="ml-1" />
                </li>
              </ul>
            </div>
          </div>
          <div className="ml-auto">
            <Text as="div" variant="studio/title/md">
              0 - 100 Spins
            </Text>
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-row flex-nowrap">
        <Button variant="secondary" onClick={close}>
          Close
        </Button>
        {!isUnlimited() && (
          <Button
            className="ml-auto"
            variant="primary"
            onClick={isPro() ? handleProCheckout : handleUnlimitedCheckout}
          >
            {'Upgrade plan'}
          </Button>
        )}
      </div>
    </Modal>
  );
}
